import React, { useState, useEffect } from 'react';
import {
  Button,
  IconButton,
  CircularProgress,
  withStyles,
  Tooltip,
} from '@material-ui/core';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import CancelIcon from '@material-ui/icons/Cancel';
import TimerIcon from '@material-ui/icons/Timer';
import DirectionsRunIcon from '@material-ui/icons/DirectionsRun';
import ErrorIcon from '@material-ui/icons/Error';
import VisibilityIcon from '@material-ui/icons/Visibility';
import PlayCircleOutlineIcon from '@material-ui/icons/PlayCircleOutline';
import RestoreIcon from '@material-ui/icons/Restore';
import { format } from 'date-fns';
import { getAutomations, setAutomationShouldRun, runAutomationNow, resetAutomationStatus } from '../../services';
import ErrorMessage from '../../components/Error/ErrorMessage';
import { AgGrid } from '../../components/AgGrid';
import { ConfirmationDialog } from '../../components/ConfirmationDialog';
import classnames from 'classnames';

const EnabledCellRenderer = (props) => {
  const value = props.value;

  if (value === undefined || value === null) {
    return <span>-</span>;
  }

  if (value === true || value === 'true' || value === 1) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center' }}>
        <Tooltip title="Enabled" placement="top">
          <CheckCircleIcon style={{ color: '#4caf50', fontSize: 24 }} />
        </Tooltip>
      </div>
    );
  }
  return (
    <div style={{ display: 'flex', justifyContent: 'center' }}>
      <Tooltip title="Disabled" placement="top">
        <CancelIcon style={{ color: '#f44336', fontSize: 24 }} />
      </Tooltip>
    </div>
  );
};

const LastRunStatusCellRenderer = (props) => {
  const value = props.value?.toLowerCase();
  
  if (value === undefined || value === null) {
    return <span>-</span>;
  }
  
  if (value === 'success') {
    return (
      <div style={{ display: 'flex', justifyContent: 'center' }}>
        <Tooltip title="Completed" placement="top">
          <CheckCircleIcon style={{ color: '#4caf50', fontSize: 24 }} />
        </Tooltip>
      </div>
    );
  }
  if (value === 'dryrun') {
    return (
      <div style={{ display: 'flex', justifyContent: 'center' }}>
        <Tooltip title="Dry Run (non-prod - execution skipped)" placement="top">
          <VisibilityIcon style={{ color: '#9c27b0', fontSize: 24 }} />
        </Tooltip>
      </div>
    );
  }
  return (
    <div style={{ display: 'flex', justifyContent: 'center' }}>
      <Tooltip title="Failed" placement="top">
        <ErrorIcon style={{ color: '#f44336', fontSize: 24 }} />
      </Tooltip>
    </div>
  );
};

const LastRunTimeCellRenderer = (props) => {
  const value = props.value;
  
  if (!value) {
    return <span>-</span>;
  }

  try {
    const date = new Date(value);
    const formattedDate = format(date, 'MM/dd/yyyy hh:mm:ss a');
    
    return (
      <div style={{ display: 'flex', justifyContent: 'center' }}>
        <span>{formattedDate}</span>
      </div>
    );
  } catch (error) {
    console.error('Error parsing date:', error);
    return <span>{value}</span>;
  }
};

// Enqueues an on-demand run via POST .../run. Doesn't touch grid state on
// success - LastRunStatus/LastRunTime only change once AutomationWebJobs
// actually picks the message up, so there's nothing to optimistically update
// here, just a per-button in-flight/result indicator.
const RunNowButton = ({ data }) => {
  const [status, setStatus] = useState('idle'); // idle | running | success | error

  const isDisabled = data.enabled === false;

  const handleClick = async () => {
    setStatus('running');
    try {
      await runAutomationNow(data.automationId);
      setStatus('success');
    } catch (error) {
      setStatus('error');
    } finally {
      setTimeout(() => setStatus('idle'), 3000);
    }
  };

  let icon;
  let tooltipTitle;
  if (status === 'running') {
    icon = <CircularProgress size={20} />;
    tooltipTitle = 'Queuing run...';
  } else if (status === 'success') {
    icon = <CheckCircleIcon style={{ color: '#4caf50', fontSize: 24 }} />;
    tooltipTitle = 'Run queued';
  } else if (status === 'error') {
    icon = <ErrorIcon style={{ color: '#f44336', fontSize: 24 }} />;
    tooltipTitle = 'Failed to queue run';
  } else {
    icon = <PlayCircleOutlineIcon style={{ color: isDisabled ? '#bdbdbd' : '#315382', fontSize: 24 }} />;
    tooltipTitle = isDisabled ? 'Enable this automation before running it manually' : 'Run this automation now';
  }

  return (
    <Tooltip title={tooltipTitle} placement="top">
      <span>
        <IconButton size="small" disabled={isDisabled || status === 'running'} onClick={handleClick}>
          {icon}
        </IconButton>
      </span>
    </Tooltip>
  );
};

// Recovers a stuck Automations.Status (e.g. the WebJob process was killed
// mid-run and never reached the code that flips it back to Standby) - only
// enabled while Status is actually "Running", and always behind a
// confirmation, since resetting a genuinely in-progress automation would let
// a second instance start and run concurrently with it.
const ResetStatusButton = ({ data, onResetSuccess }) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [isResetting, setIsResetting] = useState(false);
  const [error, setError] = useState(null);

  const isRunning = data.status === 'Running';

  const handleConfirm = async () => {
    setIsResetting(true);
    setError(null);
    try {
      await resetAutomationStatus(data.automationId);
      setDialogOpen(false);
      onResetSuccess(data.automationId);
    } catch (err) {
      setError(typeof err === 'string' ? err : 'There was an error resetting this automation.');
    } finally {
      setIsResetting(false);
    }
  };

  return (
    <>
      <ConfirmationDialog
        visible={dialogOpen}
        setVisibility={() => setDialogOpen(false)}
        title="Reset Automation Status"
        isSubmitting={isResetting}
        submitMessage={error}
        cancelButton={
          <Button variant="contained" onClick={() => setDialogOpen(false)} disabled={isResetting}>
            Cancel
          </Button>
        }
        acceptButton={
          <Button
            variant="contained"
            style={{ backgroundColor: '#f44336', color: '#fff' }}
            onClick={handleConfirm}
            disabled={isResetting}
          >
            Yes, reset it
          </Button>
        }
      >
        <div>
          <strong>{data.friendlyJobName}</strong> is currently showing as <strong>Running</strong>.
        </div>
        <div style={{ marginTop: 10 }}>
          Only reset this if you're sure it isn't actually running right now - for example, the
          process was interrupted and never got to update its own status. Resetting a job that's
          genuinely still in progress could let a second instance start and run concurrently
          with it.
        </div>
      </ConfirmationDialog>

      <Tooltip
        title={isRunning ? "Reset status (only if you're sure this isn't actually running)" : 'Only available while Status is Running'}
        placement="top"
      >
        <span>
          <IconButton size="small" disabled={!isRunning} onClick={() => setDialogOpen(true)}>
            <RestoreIcon style={{ color: isRunning ? '#f44336' : '#bdbdbd', fontSize: 24 }} />
          </IconButton>
        </span>
      </Tooltip>
    </>
  );
};

const ActionsCellRenderer = (props) => {
  const { data, onResetSuccess } = props;

  if (!data) {
    return null;
  }

  return (
    <div style={{ display: 'flex', justifyContent: 'center' }}>
      <RunNowButton data={data} />
      <ResetStatusButton data={data} onResetSuccess={onResetSuccess} />
    </div>
  );
};

const StatusCellRenderer = (props) => {
  const value = props.value?.toLowerCase();
  
  if (value === undefined || value === null) {
    return <span>-</span>;
  }
  
  if (value === 'running') {
    return (
      <div style={{ display: 'flex', justifyContent: 'center' }}>
        <Tooltip title="Running" placement="top">
          <DirectionsRunIcon style={{ color: '#4caf50', fontSize: 24 }} />
        </Tooltip>
      </div>
    );
  }
  return (
    <div style={{ display: 'flex', justifyContent: 'center' }}>
      <Tooltip title="Standby" placement="top">
        <TimerIcon style={{ color: '#ff9800', fontSize: 24 }} />
      </Tooltip>
    </div>
  );
};

const buildHeadCells = (onResetSuccess) => [
  {
    headerName: '',
    field: 'automationId',
    width: 50,
    minWidth: 50,
    maxWidth: 50,
    resizable: false,
    sortable: false,
    filter: false,
    suppressMenu: true,
    checkboxSelection: true,
    headerCheckboxSelection: true,
    headerCheckboxSelectionFilteredOnly: true,
    // A dedicated selection-only column - clicking its checkbox is exempt
    // from the grid's row-click navigation (see AgGrid.js _handleCellClick,
    // which skips navigation whenever colDef.checkboxSelection is set) so
    // selecting rows for the bulk Enable/Disable buttons never navigates.
    cellRenderer: () => '',
  },
  { headerName: 'Job Name', field: 'friendlyJobName', width: 500, resizable: true },
  {
    headerName: 'Enabled',
    field: 'enabled',
    width: 125,
    resizable: true,
    cellRenderer: 'enabledCellRenderer',
    cellStyle: { display: 'flex', justifyContent: 'center', alignItems: 'center' },
    filter: 'agSetColumnFilter',
    filterParams: {
      values: [true, false],
      valueFormatter: (params) => params.value ? 'Enabled' : 'Disabled',
      cellRenderer: 'enabledCellRenderer'
    }
  },
  { 
    headerName: 'Status', 
    field: 'status', 
    width: 125, 
    resizable: true,
    cellRenderer: 'statusCellRenderer',
    cellStyle: { display: 'flex', justifyContent: 'center', alignItems: 'center' },
    filter: 'agSetColumnFilter',
    filterParams: {
      values: ['Running', 'Standby'],
      cellRenderer: 'statusCellRenderer'
    }
  },
  { 
    headerName: 'Last Run Status', 
    field: 'lastRunStatus', 
    width: 125, 
    resizable: true,
    cellRenderer: 'lastRunStatusCellRenderer',
    cellStyle: { display: 'flex', justifyContent: 'center', alignItems: 'center' },
    filter: 'agSetColumnFilter',
    filterParams: {
      values: ['Success', 'Failure', 'DryRun'],
      cellRenderer: 'lastRunStatusCellRenderer'
    }
  },
  { 
    headerName: 'Last Run Time', 
    field: 'lastRunTime', 
    width: 200, 
    resizable: true,
    type: 'dateColumn',
    filter: 'agDateColumnFilter',
    filterParams: {
      comparator: (filterLocalDateAtMidnight, cellValue) => {
        const cellDate = new Date(cellValue);
        if (cellDate < filterLocalDateAtMidnight) {
          return -1;
        } else if (cellDate > filterLocalDateAtMidnight) {
          return 1;
        }
        return 0;
      }
    },
    cellRenderer: 'lastRunTimeCellRenderer',
    cellStyle: { display: 'flex', justifyContent: 'center', alignItems: 'center' },
    valueGetter: (params) => {
      return params.data?.lastRunTime ? new Date(params.data.lastRunTime) : null;
    }
  },
  {
    headerName: 'Actions',
    field: 'automationId',
    width: 130,
    resizable: false,
    sortable: false,
    filter: false,
    suppressMenu: true,
    // editable (not checkboxSelection) exempts this column from the grid's
    // row-click navigation (see AgGrid.js _handleCellClick) without adding
    // an actual ag-Grid checkbox widget to the cell.
    editable: true,
    cellRenderer: 'actionsCellRenderer',
    cellRendererParams: { onResetSuccess },
    cellStyle: { display: 'flex', justifyContent: 'center', alignItems: 'center' },
  },
];

const styles = theme => ({});

const AutomationsGrid = withStyles(styles)(props => {
  const [automations, setAutomations] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(false);

  useEffect(() => {
    const fetchAutomations = async () => {
      try {
        const automationsData = await getAutomations();
        setAutomations(automationsData);
      } catch (error) {
        setIsError(true);
      } finally {
        setIsLoading(false);
      }
    };

    fetchAutomations();
  }, []);

  // The backend has already confirmed the reset there - this just reflects it
  // in the grid without waiting for the next full refetch.
  const handleResetSuccess = (automationId) => {
    setAutomations(prev =>
      prev.map(a => (a.automationId === automationId ? { ...a, status: 'Standby' } : a))
    );
  };

  const [selectedAutomations, setSelectedAutomations] = useState([]);
  const [isBulkUpdating, setIsBulkUpdating] = useState(false);
  const [bulkUpdateError, setBulkUpdateError] = useState(null);

  const handleSelectionChanged = (params) => {
    setSelectedAutomations(params.api.getSelectedRows());
  };

  // Optimistically flips every selected row, fires the PATCHes in parallel,
  // then re-fetches from the server if any failed so the grid never keeps
  // showing an optimistic state that didn't actually save.
  const handleBulkSetShouldRun = async (newValue) => {
    if (!selectedAutomations.length || isBulkUpdating) {
      return;
    }

    const ids = selectedAutomations.map(a => a.automationId);
    setBulkUpdateError(null);
    setIsBulkUpdating(true);
    setAutomations(prev =>
      prev.map(a => (ids.includes(a.automationId) ? { ...a, enabled: newValue } : a))
    );

    try {
      await Promise.all(ids.map(id => setAutomationShouldRun(id, newValue)));
    } catch (error) {
      setBulkUpdateError('There was an error updating one or more automations.');
      try {
        const refreshed = await getAutomations();
        setAutomations(refreshed);
      } catch (refreshError) {
        // leave the optimistic state in place - the error message above still shows
      }
    } finally {
      setIsBulkUpdating(false);
    }
  };

  const gridOptions = {
    suppressColumnVirtualisation: true,
    rowSelection: 'multiple',
    suppressRowClickSelection: true,
    defaultColDef: {
      resizable: true,
      suppressAutoSize: false,
      flex: 1
    },
    frameworkComponents: {
      enabledCellRenderer: EnabledCellRenderer,
      actionsCellRenderer: ActionsCellRenderer,
      statusCellRenderer: StatusCellRenderer,
      lastRunStatusCellRenderer: LastRunStatusCellRenderer,
      lastRunTimeCellRenderer: LastRunTimeCellRenderer
    },
    pagination: true,
    paginationPageSize: 25,
    paginationPageSizeSelector: [10, 25, 50, 100],
    suppressPaginationPanel: false
  };

  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', width: '100%', padding: '20px' }}>
        <CircularProgress />
      </div>
    );
  }

  if (isError) {
    return <ErrorMessage message="There was an error fetching automations." />;
  }

  return (
      <div className={classnames('flex-1', 'card', 'border-left-green') }>
      <AgGrid
        gridData={automations}
        gridOptions={gridOptions}
        columnDefs={buildHeadCells(handleResetSuccess)}
        showCreateButton={false}
        redirectOnClick={true}
        editOnClick={false}
        handleCellClick={evt => {
          // console.log('Clicked row data:', evt.data); // Temporary log to see what fields are available
          const automationId = evt.data.automationId;
          // console.log(evt.data);
          if (automationId) {
            window.location = `${window.location.href}/${automationId}`;
          } else {
            console.error('No automation ID found in row data:', evt.data);
          }
        }}
        rowClassRules={{}}
        extraGridProps={{ onSelectionChanged: handleSelectionChanged }}
        headerContent={
          <div style={{ display: 'flex', alignItems: 'center', marginRight: 15 }}>
            <Button
              style={{ backgroundColor: '#4caf50', color: '#fff', marginRight: 10 }}
              variant="contained"
              disabled={!selectedAutomations.length || isBulkUpdating}
              onClick={() => handleBulkSetShouldRun(true)}
            >
              Enable Selected
            </Button>
            <Button
              style={{ backgroundColor: '#f44336', color: '#fff', marginRight: 10 }}
              variant="contained"
              disabled={!selectedAutomations.length || isBulkUpdating}
              onClick={() => handleBulkSetShouldRun(false)}
            >
              Disable Selected
            </Button>
            {isBulkUpdating && <CircularProgress size={20} />}
            {bulkUpdateError && <span style={{ color: '#f44336', marginLeft: 10 }}>{bulkUpdateError}</span>}
          </div>
        }
      />
    </div>
  );
});

export { AutomationsGrid };
